import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Search,
  Download,
  ExternalLink,
  Star,
  Sparkles,
  Flame,
  Lock,
  ArrowRight,
  Compass,
  Rocket,
  Shield,
  Layers,
  Palette,
  CheckCircle2,
  Award,
  BookOpen,
} from 'lucide-react';
import { externalResources, filterCategories, frameworkStages } from '../data/externalResources';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageIcons = {
  Foundation: Compass,
  Validation: CheckCircle2,
  Credibility: Award,
  Brand: Palette,
  Systems: Layers,
  Protection: Shield,
  Launch: Rocket,
};

const tagColors = {
  Foundation: 'bg-[#EFF6FF] text-[#1D4ED8]',
  Validation: 'bg-purple-100 text-purple-700',
  Credibility: 'bg-amber-100 text-amber-700',
  Brand: 'bg-pink-100 text-pink-700',
  Systems: 'bg-emerald-100 text-emerald-700',
  Protection: 'bg-red-100 text-red-700',
  Launch: 'bg-[#FFF7ED] text-[#F97316]',
  'AI Tools': 'bg-violet-100 text-violet-700',
  Marketing: 'bg-cyan-100 text-cyan-700',
  Productivity: 'bg-slate-100 text-slate-700',
  Finance: 'bg-green-100 text-green-700',
  'Website Tools': 'bg-indigo-100 text-indigo-700',
};

// LocalStorage favorites
const FAVS_KEY = 'dca_resource_favs';

const getFavicon = (url) => {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch {
    return null;
  }
};

function ResourceCard({ resource, isFav, onToggleFav, dataIndex }) {
  const favicon = getFavicon(resource.url);
  return (
    <div
      className="group relative bg-white border border-[#E2E8F0] rounded-xl p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#1D4ED8]/40"
      data-testid={`resource-card-${dataIndex}`}
    >
      {/* Top row: logo + favorite */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center overflow-hidden shrink-0">
            {favicon ? (
              <img src={favicon} alt={resource.name} className="w-7 h-7" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <ExternalLink size={18} className="text-[#94A3B8]" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-[#0A0F1C] text-base leading-tight truncate">{resource.name}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tagColors[resource.category] || 'bg-slate-100 text-slate-700'}`}>
                {resource.category}
              </span>
              {resource.tag && resource.tag !== resource.category && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${tagColors[resource.tag] || 'bg-slate-100 text-slate-700'}`}>
                  {resource.tag}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggleFav(resource.name)}
          className={`p-1.5 rounded-full transition-colors ${isFav ? 'text-[#F97316]' : 'text-[#CBD5E1] hover:text-[#F97316]'}`}
          aria-label="Favorite"
          data-testid={`fav-btn-${dataIndex}`}
        >
          <Star size={16} fill={isFav ? '#F97316' : 'none'} />
        </button>
      </div>

      {/* Badges */}
      {(resource.recommended || resource.popular) && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {resource.recommended && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#3B82F6] text-white">
              <Sparkles size={10} /> David Ashley Recommended
            </span>
          )}
          {resource.popular && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#F97316]">
              <Flame size={10} /> Most Popular
            </span>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-[#475569] leading-relaxed mb-4 line-clamp-3">{resource.description}</p>

      {/* CTA */}
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:text-white hover:bg-[#1D4ED8] border border-[#1D4ED8] px-4 py-2 rounded-lg transition-all w-full group-hover:bg-[#1D4ED8] group-hover:text-white"
        data-testid={`visit-${dataIndex}`}
      >
        Visit Resource <ExternalLink size={14} />
      </a>
    </div>
  );
}

function FeaturedDCAResource({ resource, index }) {
  return (
    <div
      className="relative bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white border border-[#1D4ED8]/30 rounded-xl p-6 hover:shadow-2xl hover:shadow-[#1D4ED8]/20 transition-all"
      data-testid={`featured-resource-${index}`}
    >
      <div className="absolute top-4 right-4">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F97316] text-white">
          <Award size={10} /> By David Ashley
        </span>
      </div>
      <div className="w-12 h-12 rounded-lg bg-[#1D4ED8]/20 border border-[#1D4ED8]/40 flex items-center justify-center mb-4">
        <Download size={20} className="text-[#60A5FA]" />
      </div>
      <h3 className="text-xl font-bold mb-2 leading-tight">{resource.title}</h3>
      <p className="text-sm text-[#94A3B8] mb-4 leading-relaxed">{resource.description}</p>
      <div className="flex items-center gap-2 mb-4 text-xs text-[#94A3B8]">
        <Lock size={12} /> Free with email signup
      </div>
      <Link
        to={`/resources/${resource.slug}`}
        className="inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-semibold w-full transition-colors"
        data-testid={`featured-cta-${index}`}
      >
        Get Free Resource <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function ResourcesPage() {
  const [dcaResources, setDcaResources] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All Resources');
  const [query, setQuery] = useState('');
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAVS_KEY) || '[]'); } catch { return []; }
  });
  const [showAITools, setShowAITools] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/resources`)
      .then((res) => setDcaResources(res.data || []))
      .catch(() => toast.error('Could not load David Ashley resources'));
  }, []);

  useEffect(() => {
    localStorage.setItem(FAVS_KEY, JSON.stringify(favs));
  }, [favs]);

  const toggleFav = (name) => {
    setFavs((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  // Filter logic
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return externalResources.filter((r) => {
      const matchesQuery = !q || r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || (r.tag || '').toLowerCase().includes(q);
      const matchesFilter =
        activeFilter === 'All Resources' ||
        r.category === activeFilter ||
        r.tag === activeFilter;
      return matchesQuery && matchesFilter;
    });
  }, [query, activeFilter]);

  const aiTools = useMemo(() => externalResources.filter((r) => r.tag === 'AI Tools'), []);

  // Group filtered resources by framework stage when "All" is selected
  const isFrameworkView = activeFilter === 'All Resources' && !query.trim();
  const groupedByStage = useMemo(() => {
    if (!isFrameworkView) return null;
    const map = {};
    frameworkStages.forEach((s) => { map[s.key] = []; });
    filtered.forEach((r) => {
      if (map[r.category]) map[r.category].push(r);
    });
    return map;
  }, [filtered, isFrameworkView]);

  return (
    <Layout
      title="Free Tools & Resources for Entrepreneurs"
      description="The exact tools, platforms, and resources David C. Ashley uses to help entrepreneurs launch and grow credible, profitable businesses."
    >
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="resources-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-16 sm:py-20">
          {/* Background grid */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}></div>
          {/* Glow orbs */}
          <div className="absolute top-10 -left-20 w-72 h-72 rounded-full bg-[#1D4ED8] opacity-30 blur-3xl"></div>
          <div className="absolute bottom-10 -right-20 w-72 h-72 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>

          <div className="container-custom relative z-10 text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-sm font-semibold rounded-full mb-5" data-testid="resources-badge">
              <Sparkles size={14} className="text-[#FB923C]" /> Entrepreneur Success Center
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight" data-testid="resources-title">
              Free Tools & Resources to Help You{' '}
              <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">Launch and Grow</span>{' '}
              Your Business
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-8 leading-relaxed">
              Explore the exact tools, platforms, and resources recommended to help entrepreneurs build credible, profitable businesses from the ground up.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-xl mx-auto">
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">{externalResources.length}+</div>
                <div className="text-xs sm:text-sm text-[#94A3B8] mt-1">Curated Tools</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">7</div>
                <div className="text-xs sm:text-sm text-[#94A3B8] mt-1">Framework Stages</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">100%</div>
                <div className="text-xs sm:text-sm text-[#94A3B8] mt-1">Free to Start</div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED DCA RESOURCES */}
        {dcaResources.length > 0 && (
          <section className="container-custom py-10 sm:py-14" data-testid="dca-featured-section">
            <div className="flex items-center gap-2 mb-2">
              <Award size={18} className="text-[#F97316]" />
              <span className="text-sm font-bold text-[#F97316] uppercase tracking-wide">Exclusive - Built by David</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0F1C] mb-2">Free Resources from David C. Ashley</h2>
            <p className="text-[#475569] mb-6 max-w-2xl">Battle-tested checklists and frameworks pulled from years of helping entrepreneurs launch and scale.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {dcaResources.slice(0, 3).map((r, i) => (
                <FeaturedDCAResource key={r.resource_id} resource={r} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* SEARCH + FILTERS */}
        <section className="container-custom pb-6" data-testid="resources-controls">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="relative mb-4">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search 60+ tools. Try 'CRM', 'AI', 'logo', 'email'…"
                className="pl-10 h-11 text-base"
                data-testid="resources-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {filterCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  className={`text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border transition-all ${
                    activeFilter === cat
                      ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]'
                      : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'
                  }`}
                  data-testid={`filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* AI TOOLS SPOTLIGHT */}
        {!query && activeFilter === 'All Resources' && (
          <section className="container-custom mb-8" data-testid="ai-spotlight">
            <div className="bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200 rounded-xl p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={16} className="text-violet-600" />
                    <span className="text-xs font-bold text-violet-600 uppercase tracking-wide">AI Tools Spotlight</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A0F1C]">Build faster with the best AI tools</h3>
                </div>
                <button
                  onClick={() => setShowAITools((s) => !s)}
                  className="text-sm font-semibold text-violet-600 hover:underline"
                  data-testid="toggle-ai-spotlight"
                >
                  {showAITools ? 'Hide' : `View all ${aiTools.length}`}
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(showAITools ? aiTools : aiTools.slice(0, 3)).map((r, i) => (
                  <ResourceCard key={r.name} resource={r} isFav={favs.includes(r.name)} onToggleFav={toggleFav} dataIndex={`ai-${i}`} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* RESULTS */}
        <section className="container-custom pb-16">
          {filtered.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
              <BookOpen size={40} className="text-[#CBD5E1] mx-auto mb-3" />
              <p className="text-[#475569]">No resources match your search. Try a different keyword or filter.</p>
            </div>
          ) : isFrameworkView ? (
            // Framework view (grouped by stage)
            <div className="space-y-12">
              {frameworkStages.map((stage) => {
                const items = groupedByStage[stage.key];
                if (!items || items.length === 0) return null;
                const Icon = stageIcons[stage.key];
                return (
                  <div key={stage.key} data-testid={`stage-section-${stage.key.toLowerCase()}`}>
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-5">
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-lg bg-[#EFF6FF] flex items-center justify-center shrink-0">
                          {Icon && <Icon size={20} className="text-[#1D4ED8]" />}
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-[#0A0F1C]">{stage.key}</h2>
                          <p className="text-sm text-[#475569] mt-0.5 max-w-xl">{stage.desc}</p>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-[#94A3B8] sm:self-center shrink-0">{items.length} tools</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {items.map((r, i) => (
                        <ResourceCard
                          key={r.name}
                          resource={r}
                          isFav={favs.includes(r.name)}
                          onToggleFav={toggleFav}
                          dataIndex={`${stage.key}-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Filtered grid (single layer)
            <div>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-[#0A0F1C]" data-testid="results-heading">
                  {activeFilter === 'All Resources' ? 'Search Results' : activeFilter}
                </h2>
                <span className="text-sm text-[#475569]" data-testid="results-count">
                  {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((r, i) => (
                  <ResourceCard
                    key={r.name}
                    resource={r}
                    isFav={favs.includes(r.name)}
                    onToggleFav={toggleFav}
                    dataIndex={i}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* FAVORITES SUMMARY */}
        {favs.length > 0 && (
          <section className="container-custom pb-12" data-testid="favs-summary">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Star size={20} className="text-[#F97316]" fill="#F97316" />
                <div>
                  <div className="font-bold text-[#0A0F1C]">{favs.length} saved tool{favs.length !== 1 ? 's' : ''}</div>
                  <p className="text-xs text-[#475569]">Your favorites are saved locally - come back anytime.</p>
                </div>
              </div>
              <button
                onClick={() => setFavs([])}
                className="text-sm text-[#475569] hover:text-[#1D4ED8] font-medium"
                data-testid="clear-favs-btn"
              >
                Clear favorites
              </button>
            </div>
          </section>
        )}

        {/* CTA Footer */}
        <section className="bg-[#0F172A] text-white py-12">
          <div className="container-custom text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to put these tools to work?</h2>
            <p className="text-[#94A3B8] mb-6">Book a free 20-minute strategy session and David will help you pick the right stack for your stage.</p>
            <Link to="/schedule" className="btn-primary-orange inline-flex items-center gap-2" data-testid="resources-cta-btn">
              FREE 20-Minute Business Jumpstart Session <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
